import React, { useEffect, useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import COLORS from '../../../utils/constants/colors'

const GAIN = 4 // RMS (speech ≈ 0.1) → useful 0..1 range
// Mostly green; warms to amber/red only at the top of the range as a clip warning.
const FILL_GRADIENT = `linear-gradient(90deg, ${COLORS.green} 0%, ${COLORS.green} 72%, ${COLORS.darkOrange} 88%, ${COLORS.red} 100%)`

const useStyles = makeStyles({
  // A solid neutral track; colour appears ONLY in the fill, which grows from the left
  // with the live level. Empty = a clean neutral bar (no gradient, no edge artefacts).
  track: {
    position: 'relative',
    height: '10px',
    borderRadius: '5px',
    overflow: 'hidden',
  },
  fill: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '0%',
    backgroundImage: FILL_GRADIENT,
    backgroundRepeat: 'no-repeat',
    transition: 'width 0.06s linear',
  },
})

// Input-level meter for the commentator's mic. Reads the LiveKit local track's
// MediaStreamTrack via a Web Audio AnalyserNode (RMS, peak-hold decay) and grows a
// fill div from the left directly through a ref — no React state, so the ~60 Hz updates
// never re-render the page. The fill's gradient is sized to the FULL track width, so red
// only appears once the level is genuinely high (not at every fill width).
const AudioLevelMeter = ({ track, width = '200px', dark = false }) => {
  const classes = useStyles()
  const fillRef = useRef(null)

  useEffect(() => {
    const setLevel = (level) => {
      if (fillRef.current) fillRef.current.style.width = `${Math.min(100, level * 100)}%`
    }

    const mediaStreamTrack = track?.mediaStreamTrack
    if (!mediaStreamTrack || mediaStreamTrack.readyState === 'ended') {
      setLevel(0)
      return undefined
    }

    let raf = null
    let ctx = null
    let display = 0
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext
      ctx = new Ctx()
      const source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]))
      const analyser = ctx.createAnalyser()
      analyser.fftSize = 512
      source.connect(analyser)
      const buf = new Uint8Array(analyser.fftSize)
      const tick = () => {
        analyser.getByteTimeDomainData(buf)
        let sumSq = 0
        for (let i = 0; i < buf.length; i += 1) {
          const v = (buf[i] - 128) / 128
          sumSq += v * v
        }
        const target = Math.min(1, Math.sqrt(sumSq / buf.length) * GAIN)
        // Fast attack, slow decay → a steady, readable level rather than a flicker.
        display = target > display ? target : display * 0.86 + target * 0.14
        setLevel(display)
        raf = requestAnimationFrame(tick)
      }
      tick()
    } catch {
      /* Web Audio unsupported → meter stays idle */
    }

    return () => {
      if (raf) cancelAnimationFrame(raf)
      if (ctx) ctx.close().catch(() => {})
      setLevel(0)
    }
  }, [track])

  return (
    <div
      className={classes.track}
      style={{
        width,
        background: dark ? '#3b4049' : COLORS.borderGrey,
        border: dark ? '1px solid rgba(255, 255, 255, 0.16)' : 'none',
      }}
    >
      <div ref={fillRef} className={classes.fill} style={{ backgroundSize: `${width} 100%` }} />
    </div>
  )
}

export default AudioLevelMeter
